// src/pages/Tickets.jsx
import { normalizarTexto } from '../utils/textoBusqueda';
import { esperaPorMi } from '../utils/ticketsFiltros';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { List as ListIcon } from 'lucide-react';
import { Search, Plus, LayoutGrid, SlidersHorizontal, ChevronDown, UserCheck, FileText } from '../components/icons';

import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { useNotificationContext } from '../contexts/NotificationContext';
import InvGateTicketCard from '../components/InvGateTicketCard';
import InvGateSidebarActions from '../components/InvGateSidebarActions';
import CreateTicketModal from '../components/CreateTicketModal';
import TicketsTabla from '../components/TicketsTabla';
import Paginador from '../components/Paginador';

export default function Tickets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { alertSuccess, alertError } = useNotificationContext();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [subAsignados, setSubAsignados] = useState('atender'); // dentro de 'Asignados a mí': 'atender' (sin resolver) | 'resueltos'
  const [selectedTab, setSelectedTab] = useState('all'); // 'open' | 'waiting' | 'assigned' | 'resolved' | 'tasks' | 'esperandoUsuario' | 'unassigned' | 'all'
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
  const [filtroPrioridad, setFiltroPrioridad] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroAgente, setFiltroAgente] = useState(''); // '' | 'sin' | id del agente
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('self');
  const [showNewMenu, setShowNewMenu] = useState(false);

  // Derivaciones y tareas pendientes
  const [derivaciones, setDerivaciones] = useState([]);

  // Cargar tickets desde la API
  const cargarTickets = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/tickets');
      const data = Array.isArray(response.data) ? response.data : [];
      setTickets(data);
    } catch (error) {
      console.error('Error al cargar tickets:', error.message);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar derivaciones y aprobaciones
  const cargarDerivaciones = useCallback(async () => {
    try {
      const response = await api.get('/tickets/derivaciones/pendientes');
      setDerivaciones(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Endpoint fallback
      setDerivaciones([]);
    }
  }, []);

  useEffect(() => {
    cargarTickets();
    cargarDerivaciones();
  }, [cargarTickets, cargarDerivaciones]);

  // Responder derivaciones desde el panel lateral
  const handleResponderDerivacion = async (e, ticketId, aceptar) => {
    if (e) e.stopPropagation();
    try {
      await api.post(`/tickets/${ticketId}/responder-derivacion`, { aceptar });
      if (alertSuccess) alertSuccess(aceptar ? 'Aprobación confirmada' : 'Aprobación rechazada');
      cargarTickets();
      cargarDerivaciones();
    } catch (err) {
      if (alertError) alertError(err.response?.data?.message || 'Error al responder derivación');
    }
  };

  // Autoasignarse un ticket sin responsable (misma regla de perfil SAP que el detalle)
  const puedeAutoasignar = (t) => {
    const rol = user?.role?.toLowerCase();
    if (rol === 'admin') return true;
    if (rol !== 'soporte') return false;
    const esSapTicket = String(t.categoria?.nombre || t.categoria || '').toUpperCase().includes('SAP');
    return esSapTicket === ((user?.soportePerfil || '').toUpperCase() === 'SAP');
  };
  const handleAutoasignar = async (e, ticketId) => {
    if (e) e.stopPropagation();
    try {
      await api.put(`/tickets/${ticketId}/asignar`, { soporteId: user.id });
      if (alertSuccess) alertSuccess(`Ticket #${ticketId} asignado a ti`);
      cargarTickets();
    } catch (err) {
      if (alertError) alertError(err.response?.data?.message || 'No se pudo asignar el ticket');
    }
  };

  // Completar tarea
  const handleCompleteTask = async (e, ticketId) => {
    if (e) e.stopPropagation();
    try {
      await api.patch(`/tickets/${ticketId}`, { estado: 'Resuelto' });
      if (alertSuccess) alertSuccess('Tarea completada exitosamente');
      cargarTickets();
    } catch (err) {
      if (alertError) alertError('Error al completar tarea');
    }
  };

  // Eliminar ticket
  const handleDeleteTicket = async (e, ticketId) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`¿Estás seguro de eliminar el ticket #${ticketId}?`)) return;
    try {
      await api.delete(`/tickets/${ticketId}`);
      if (alertSuccess) alertSuccess(`Ticket #${ticketId} eliminado correctamente.`);
      cargarTickets();
    } catch (error) {
      if (alertError) alertError('Error al eliminar el ticket.');
    }
  };

  // Accesos desde el menu lateral: /tickets?tab=assigned abre "asignados a mi" y
  // /tickets?action=new-user abre el formulario para crear un ticket a un usuario.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    setSelectedTab(tab === 'assigned' ? 'assigned' : 'all');
    if (params.get('action') === 'new-user') {
      setModalMode('other');
      setIsModalOpen(true);
      navigate('/tickets', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Estadísticas calculadas para la cinta superior estilo InvGate
  const stats = useMemo(() => {
    const currentUserId = user?.id;
    const currentEmail = user?.email?.toLowerCase();

    // Misma regla que la lista de la pestaña «Esperando por mí» (utils/ticketsFiltros.js).
    const waitingForMe = tickets.filter(t => esperaPorMi(t, user)).length;

    const assignedToMe = tickets.filter(t => {
      if (t.estado === 'Resuelto' || t.estado === 'Cerrado') return false;
      return t.asignadoAId === currentUserId || t.asignadoA?.id === currentUserId || t.asignadoA?.email?.toLowerCase() === currentEmail;
    }).length;

    const esCerrado = (t) => t.estado === 'Resuelto' || t.estado === 'Cerrado';
    const resolved = tickets.filter(esCerrado).length;
    const open = tickets.filter(t => !esCerrado(t)).length;
    const tasks = tickets.filter(t => t.estado === 'En Proceso' || t.estado === 'Asignado').length;
    const esperandoUsuario = tickets.filter(t => t.estado === 'Esperando Usuario').length;
    const unassigned = tickets.filter(t => !t.asignadoA && t.estado === 'Pendiente').length;
    const total = tickets.length;

    const esMioTk = (t) => t.asignadoAId === currentUserId || t.asignadoA?.id === currentUserId || t.asignadoA?.email?.toLowerCase() === currentEmail;
    const subCounts = {
      atender: tickets.filter(t => esMioTk(t) && !esCerrado(t)).length,
      resueltos: tickets.filter(t => esMioTk(t) && esCerrado(t)).length,
    };

    return { waitingForMe, assignedToMe, resolved, open, tasks, esperandoUsuario, unassigned, total, subCounts };
  }, [tickets, derivaciones, user]);

  // Filtrado y ordenamiento de tickets
  const filteredBase = useMemo(() => {
    let result = [...tickets];
    const currentUserId = user?.id;
    const currentEmail = user?.email?.toLowerCase();

    // 1. Filtrado por Pestaña InvGate
    const esCerradoTk = (t) => t.estado === 'Resuelto' || t.estado === 'Cerrado';
    if (selectedTab === 'open') {
      result = result.filter(t => !esCerradoTk(t));
    } else if (selectedTab === 'waiting') {
      // Solo lo mío que me toca atender: igual que el número de la tarjeta. Antes traía además lo pendiente de otros agentes.
      result = result.filter(t => esperaPorMi(t, user));
    } else if (selectedTab === 'assigned') {
      result = result.filter(t => {
        const esMio = t.asignadoAId === currentUserId || t.asignadoA?.id === currentUserId || t.asignadoA?.email?.toLowerCase() === currentEmail;
        if (!esMio) return false;
        if (subAsignados === 'resueltos') return esCerradoTk(t);
        return !esCerradoTk(t); // 'atender': todo lo asignado que sigue sin resolver
      });
    } else if (selectedTab === 'resolved') {
      result = result.filter(esCerradoTk);
    } else if (selectedTab === 'tasks') {
      result = result.filter(t => t.estado === 'En Proceso' || t.estado === 'Asignado');
    } else if (selectedTab === 'esperandoUsuario') {
      result = result.filter(t => t.estado === 'Esperando Usuario');
    } else if (selectedTab === 'unassigned') {
      result = result.filter(t => !t.asignadoA && t.estado === 'Pendiente');
    }

    // 2. Filtrado por Búsqueda
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => {
        const idMatch = String(t.id).includes(q);
        const descMatch = normalizarTexto(t.descripcion).includes(normalizarTexto(q));
        const userMatch = normalizarTexto(t.creadoPor?.nombre).includes(normalizarTexto(q)) || normalizarTexto(t.nombreSolicitante).includes(normalizarTexto(q));
        const catMatch = normalizarTexto(t.categoria?.nombre).includes(normalizarTexto(q)) || normalizarTexto(t.categoria).includes(normalizarTexto(q));
        return idMatch || descMatch || userMatch || catMatch;
      });
    }

    // 2b. Filtros de la barra superior
    if (filtroPrioridad) result = result.filter(t => (t.prioridad || '').toLowerCase() === filtroPrioridad.toLowerCase());
    if (filtroEstado) result = result.filter(t => t.estado === filtroEstado);
    if (filtroCategoria) result = result.filter(t => (t.categoria?.nombre || t.categoria || 'Soporte General') === filtroCategoria);
    if (filtroEmpresa) result = result.filter(t => (t.empresa || 'Sin empresa') === filtroEmpresa);

    // 3. Siempre por llegada: el más reciente primero
    result.sort((a, b) => new Date(b.fechaCreacion || b.createdAt || 0) - new Date(a.fechaCreacion || a.createdAt || 0));

    return result;
  }, [tickets, selectedTab, subAsignados, searchQuery, user, filtroPrioridad, filtroEstado, filtroCategoria, filtroEmpresa]);

  const agenteDe = (t) => (t.asignadoA?.id ?? t.asignadoAId ?? null);
  const filteredTickets = useMemo(() => {
    if (!filtroAgente) return filteredBase;
    if (filtroAgente === 'sin') return filteredBase.filter(t => agenteDe(t) == null);
    return filteredBase.filter(t => String(agenteDe(t)) === String(filtroAgente));
  }, [filteredBase, filtroAgente]);

  // fila de agentes: cuántos tickets de la vista actual tiene cada uno
  const agentesFila = useMemo(() => {
    const m = new Map();
    let sinAsignar = 0;
    for (const t of filteredBase) {
      const id = agenteDe(t);
      if (id == null) { sinAsignar += 1; continue; }
      const cur = m.get(id) || { id, nombre: t.asignadoA?.nombre || t.asignadoA?.email || `Agente ${id}`, n: 0 };
      cur.n += 1;
      m.set(id, cur);
    }
    return { sinAsignar, lista: [...m.values()].sort((a, b) => b.n - a.n) };
  }, [filteredBase]);

  // opciones de los desplegables, a partir de los tickets cargados
  const opcionesFiltro = useMemo(() => {
    const uniq = (f) => [...new Set(tickets.map(f).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'es'));
    return {
      estados: uniq(t => t.estado),
      categorias: uniq(t => t.categoria?.nombre || t.categoria || 'Soporte General'),
      empresas: uniq(t => t.empresa || 'Sin empresa'),
    };
  }, [tickets]);
  const hayFiltros = !!(filtroPrioridad || filtroEstado || filtroCategoria || filtroEmpresa || filtroAgente);
  const limpiarFiltros = () => { setFiltroPrioridad(''); setFiltroEstado(''); setFiltroCategoria(''); setFiltroEmpresa(''); setFiltroAgente(''); };
  const esStaff = user?.role === 'admin' || user?.role === 'soporte';

  // Próximos vencimientos de SLA: el backend todavía no calcula una fecha
  // límite real por ticket (solo si el SLA ya se cumplió o no, una vez
  // resuelto). Hasta que exista ese dato, se muestran honestamente los
  // tickets sin primera respuesta como "requieren atención" -- nada de
  // cuentas regresivas inventadas.
  const dueSoonEvents = useMemo(() => {
    return tickets
      .filter(t => t.estado === 'Pendiente' && !(t.comentarios?.length > 0))
      .slice(0, 3)
      .map(t => ({
        ticketId: t.id,
        titulo: t.descripcion || 'Sin descripción',
        descripcion: 'Sin primera respuesta',
      }));
  }, [tickets]);

  // Paginado: la cuadrícula muestra 5 filas por página (según las columnas visibles); la lista, 15 tickets
  const [columnas, setColumnas] = useState(3);
  useEffect(() => {
    const calc = () => setColumnas(window.innerWidth >= 1280 ? 3 : window.innerWidth >= 640 ? 2 : 1);
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);
  const [pagina, setPagina] = useState(1);
  const porPagina = viewMode === 'grid' ? columnas * 5 : 15;
  const totalPaginas = Math.max(1, Math.ceil(filteredTickets.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const ticketsPagina = useMemo(() => filteredTickets.slice((paginaActual - 1) * porPagina, paginaActual * porPagina), [filteredTickets, paginaActual, porPagina]);
  // al cambiar la vista, las pestañas o los filtros se vuelve a la primera página
  useEffect(() => { setPagina(1); }, [selectedTab, subAsignados, searchQuery, filtroPrioridad, filtroEstado, filtroCategoria, filtroEmpresa, filtroAgente, viewMode]);
  const rangoPagina = { desde: filteredTickets.length ? (paginaActual - 1) * porPagina + 1 : 0, hasta: Math.min(paginaActual * porPagina, filteredTickets.length) };

  return (
    <div className="space-y-4 max-w-full pb-8">
      {/* ======================= 1. CINTA SUPERIOR DE ESTADÍSTICAS (INVGATE TABS) ======================= */}
      <div className="bg-white dark:bg-gray-850 rounded-2xl border border-gray-200 dark:border-gray-800 p-2 shadow-xs">
        <div className="flex gap-2 overflow-x-auto snap-x pb-1 sm:pb-0 sm:grid sm:grid-cols-4 lg:grid-cols-7 sm:overflow-visible">
          {/* 6. TODOS -- muestra el total sin filtrar (no hay lógica real de "colaboraciones" todavía) */}
          <button
            onClick={() => setSelectedTab('all')}
            className={`shrink-0 min-w-[104px] snap-start sm:min-w-0 flex flex-col items-center justify-center p-2.5 rounded-xl border transition-all ${
              selectedTab === 'all'
                ? 'bg-gray-100 dark:bg-gray-750 border-gray-300 dark:border-gray-600 shadow-xs ring-2 ring-gray-400'
                : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60'
            }`}
          >
            <span className="w-12 h-7 rounded-md bg-gray-400 dark:bg-gray-600 text-white font-black text-sm flex items-center justify-center shadow-xs">
              {stats.total}
            </span>
            <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mt-1.5 truncate">
              TODOS
            </span>
          </button>
          {/* 2. ASSIGNED TO ME */}
          <button
            onClick={() => setSelectedTab('assigned')}
            className={`shrink-0 min-w-[104px] snap-start sm:min-w-0 flex flex-col items-center justify-center p-2.5 rounded-xl border transition-all ${
              selectedTab === 'assigned'
                ? 'bg-amber-50/90 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800 shadow-xs ring-2 ring-amber-400'
                : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60'
            }`}
          >
            <span className="w-12 h-7 rounded-md bg-amber-600 text-white font-black text-sm flex items-center justify-center shadow-xs">
              {stats.assignedToMe}
            </span>
            <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mt-1.5 truncate">
              ASIGNADOS A MÍ
            </span>
          </button>

          {/* 3. RESUELTOS (Resuelto + Cerrado) */}
          <button
            onClick={() => setSelectedTab('resolved')}
            className={`shrink-0 min-w-[104px] snap-start sm:min-w-0 flex flex-col items-center justify-center p-2.5 rounded-xl border transition-all ${
              selectedTab === 'resolved'
                ? 'bg-blue-50/90 dark:bg-blue-950/30 border-blue-300 dark:border-blue-800 shadow-xs ring-2 ring-blue-400'
                : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60'
            }`}
          >
            <span className="w-12 h-7 rounded-md bg-blue-600 text-white font-black text-sm flex items-center justify-center shadow-xs">
              {stats.resolved}
            </span>
            <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mt-1.5 truncate">
              RESUELTOS
            </span>
          </button>

          {/* 4. TASKS */}
          <button
            onClick={() => setSelectedTab('tasks')}
            className={`shrink-0 min-w-[104px] snap-start sm:min-w-0 flex flex-col items-center justify-center p-2.5 rounded-xl border transition-all ${
              selectedTab === 'tasks'
                ? 'bg-yellow-50/90 dark:bg-yellow-950/30 border-yellow-300 dark:border-yellow-800 shadow-xs ring-2 ring-yellow-400'
                : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60'
            }`}
          >
            <span className="w-12 h-7 rounded-md bg-yellow-500 text-white font-black text-sm flex items-center justify-center shadow-xs">
              {stats.tasks}
            </span>
            <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mt-1.5 truncate">
              TAREAS
            </span>
          </button>

          {/* 4b. ESPERANDO AL USUARIO (SLA en pausa) */}
          <button
            onClick={() => setSelectedTab('esperandoUsuario')}
            className={`shrink-0 min-w-[104px] snap-start sm:min-w-0 flex flex-col items-center justify-center p-2.5 rounded-xl border transition-all ${
              selectedTab === 'esperandoUsuario'
                ? 'bg-purple-50/90 dark:bg-purple-950/30 border-purple-300 dark:border-purple-800 shadow-xs ring-2 ring-purple-400'
                : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60'
            }`}
          >
            <span className="w-12 h-7 rounded-md bg-purple-600 text-white font-black text-sm flex items-center justify-center shadow-xs">
              {stats.esperandoUsuario}
            </span>
            <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mt-1.5 truncate">
              ESPERANDO USUARIO
            </span>
          </button>

          {/* 1. WAITING FOR ME */}
          <button
            onClick={() => setSelectedTab('waiting')}
            className={`shrink-0 min-w-[104px] snap-start sm:min-w-0 flex flex-col items-center justify-center p-2.5 rounded-xl border transition-all ${
              selectedTab === 'waiting'
                ? 'bg-red-50/90 dark:bg-red-950/30 border-red-300 dark:border-red-800 shadow-xs ring-2 ring-red-400'
                : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60'
            }`}
          >
            <span className="w-12 h-7 rounded-md bg-red-600 text-white font-black text-sm flex items-center justify-center shadow-xs">
              {stats.waitingForMe}
            </span>
            <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mt-1.5 truncate">
              ESPERANDO POR MÍ
            </span>
          </button>

          {/* 5. UNASSIGNED */}
          <button
            onClick={() => setSelectedTab('unassigned')}
            className={`shrink-0 min-w-[104px] snap-start sm:min-w-0 flex flex-col items-center justify-center p-2.5 rounded-xl border transition-all ${
              selectedTab === 'unassigned'
                ? 'bg-indigo-50/90 dark:bg-indigo-950/30 border-indigo-300 dark:border-indigo-800 shadow-xs ring-2 ring-indigo-400'
                : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60'
            }`}
          >
            <span className="w-12 h-7 rounded-md bg-indigo-600 text-white font-black text-sm flex items-center justify-center shadow-xs">
              {stats.unassigned}
            </span>
            <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mt-1.5 truncate">
              SIN ASIGNAR
            </span>
          </button>

        </div>
      </div>

      {/* ======================= 2. TOOLBAR & BARRA DE BÚSQUEDA INVGATE ======================= */}
      <div className="bg-white dark:bg-gray-850 rounded-xl border border-gray-200 dark:border-gray-800 p-2.5 flex flex-col gap-2.5 shadow-xs">
        <div className="flex flex-wrap items-center gap-3">
        {/* Buscador de Solicitudes */}
        <div className="relative flex-1 min-w-[16rem]">
          <div className="absolute left-3.5 inset-y-0 flex items-center pointer-events-none">
            <Search className="w-4 h-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar usuarios, empresas, artículos, solicitudes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg pl-9 pr-4 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          />
        </div>

        {/* Acciones del Toolbar: Ordenamiento, Vistas y Nuevo Ticket */}
        <div className="flex flex-wrap items-center gap-2 justify-start md:justify-end">
          {/* Filtros (mismo estilo que el ordenamiento) */}
          {[
            ['Prioridad', filtroPrioridad, setFiltroPrioridad, ['Crítica', 'Alta', 'Media', 'Baja']],
            ['Estado', filtroEstado, setFiltroEstado, opcionesFiltro.estados],
            ['Categoría', filtroCategoria, setFiltroCategoria, opcionesFiltro.categorias],
            ['Empresa', filtroEmpresa, setFiltroEmpresa, opcionesFiltro.empresas],
          ].map(([etiqueta, valor, set, opciones]) => (
            <div key={etiqueta} className={`flex items-center gap-1.5 border px-3 py-1.5 rounded-lg text-xs font-semibold ${valor ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300' : 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'}`}>
              <SlidersHorizontal className="w-3.5 h-3.5 opacity-60" />
              <div className="relative">
                <select value={valor} onChange={(e) => set(e.target.value)} aria-label={`Filtrar por ${etiqueta}`}
                  style={{ backgroundImage: 'none' }}
                  className="appearance-none bg-transparent border-none outline-none cursor-pointer pr-5 text-xs font-medium max-w-[9rem]">
                  <option value="">{etiqueta}: todas</option>
                  {opciones.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <ChevronDown className="w-3 h-3 absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
              </div>
            </div>
          ))}

          {/* Toggle de Vistas (Grid vs Tabla) */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700 shrink-0">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-xs'
                  : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
              title="Vista en Tarjetas (InvGate)"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === 'table'
                  ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-xs'
                  : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
              title="Vista de Tabla"
            >
              <ListIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Botón Nuevo Ticket estilo InvGate */}
          <div className="relative">
            <button
              onClick={() => setShowNewMenu(!showNewMenu)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-xs transition-colors shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span>Nuevo</span>
              <ChevronDown className="w-3 h-3" />
            </button>

            {/* Menú de opciones de creación */}
            {showNewMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 py-1.5 z-30 text-xs">
                <button
                  onClick={() => {
                    setShowNewMenu(false);
                    setModalMode('self');
                    setIsModalOpen(true);
                  }}
                  className="w-full text-left px-3.5 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 font-medium"
                >
                  <Plus className="w-3.5 h-3.5 text-emerald-600" />
                  Crear para Mí
                </button>
                <button
                  onClick={() => {
                    setShowNewMenu(false);
                    setModalMode('other');
                    setIsModalOpen(true);
                  }}
                  className="w-full text-left px-3.5 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 font-medium"
                >
                  <UserCheck className="w-3.5 h-3.5 text-blue-600" />
                  Crear para Usuario
                </button>
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Fila de agentes */}
        {esStaff && (agentesFila.lista.length > 0 || agentesFila.sinAsignar > 0) && (
          <div className="flex items-center gap-2 overflow-x-auto pb-0.5 border-t border-gray-100 dark:border-gray-800 pt-2.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 shrink-0">Agentes</span>
            <button type="button" onClick={() => setFiltroAgente('')}
              className={`shrink-0 px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${!filtroAgente ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
              Todos
            </button>
            {agentesFila.sinAsignar > 0 && (
              <button type="button" onClick={() => setFiltroAgente(filtroAgente === 'sin' ? '' : 'sin')}
                className={`shrink-0 px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${filtroAgente === 'sin' ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                Sin asignar <span className="opacity-70">{agentesFila.sinAsignar}</span>
              </button>
            )}
            {agentesFila.lista.map(a => (
              <button key={a.id} type="button" onClick={() => setFiltroAgente(String(filtroAgente) === String(a.id) ? '' : String(a.id))}
                className={`shrink-0 inline-flex items-center gap-1.5 pl-1 pr-3 py-1 rounded-full text-xs font-semibold border transition-colors ${String(filtroAgente) === String(a.id) ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${String(filtroAgente) === String(a.id) ? 'bg-white/25 text-white' : 'bg-amber-100 text-amber-800'}`}>{(a.nombre || '?')[0].toUpperCase()}</span>
                {a.nombre.split(' ')[0]} <span className="opacity-70">{a.n}</span>
              </button>
            ))}
            {hayFiltros && (
              <button type="button" onClick={limpiarFiltros} className="shrink-0 ml-auto text-xs font-semibold text-blue-600 hover:underline">Limpiar filtros</button>
            )}
          </div>
        )}
      </div>

      {/* ======================= 3. LAYOUT PRINCIPAL DE 2 COLUMNAS (INVGATE 1:1) ======================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        {/* Columna Izquierda: Grid de Tarjetas de Tickets (8/12 = 67% o 9/12 = 75%) */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-4">
          {selectedTab === 'assigned' && (
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'atender', label: 'Sin resolver' },
                { id: 'resueltos', label: 'Resueltos' },
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSubAsignados(s.id)}
                  className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-xs font-semibold transition-colors ${subAsignados === s.id
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                >
                  {s.label}
                  <span className={`px-1.5 rounded-full text-[11px] ${subAsignados === s.id ? 'bg-white/25' : 'bg-gray-100 dark:bg-gray-700'}`}>{stats.subCounts[s.id]}</span>
                </button>
              ))}
            </div>
          )}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div key={n} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 h-48 animate-pulse flex flex-col justify-between">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  </div>
                  <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                </div>
              ))}
            </div>
          ) : viewMode === 'grid' ? (
            filteredTickets.length === 0 ? (
              <div className="bg-white dark:bg-gray-850 rounded-2xl border border-gray-200 dark:border-gray-750 p-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">No se encontraron tickets</h3>
                <p className="text-xs text-gray-500 mt-1">No hay solicitudes que coincidan con los filtros seleccionados.</p>
                <button
                  onClick={() => { setSelectedTab('all'); setSearchQuery(''); }}
                  className="mt-4 px-4 py-2 bg-blue-50 text-blue-600 dark:bg-blue-900/30 rounded-lg text-xs font-semibold hover:bg-blue-100"
                >
                  Restablecer Filtros
                </button>
              </div>
            ) : (
              <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {ticketsPagina.map((ticket) => (
                  <InvGateTicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onResponderDerivacion={handleResponderDerivacion}
                    onDeleteTicket={handleDeleteTicket}
                    onAutoasignar={handleAutoasignar}
                    puedeAutoasignar={puedeAutoasignar}
                  />
                ))}
              </div>
              <Paginador pagina={paginaActual} totalPaginas={totalPaginas} total={filteredTickets.length} desde={rangoPagina.desde} hasta={rangoPagina.hasta} onChange={setPagina} />
              </>
            )
          ) : (
            filteredTickets.length === 0 ? (
              <div className="bg-white dark:bg-gray-850 rounded-2xl border border-gray-200 dark:border-gray-750 p-12 text-center">
                <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">No se encontraron tickets</h3>
                <p className="text-xs text-gray-500 mt-1">No hay solicitudes que coincidan con los filtros seleccionados.</p>
              </div>
            ) : (
              <>
                <TicketsTabla tickets={ticketsPagina} onAutoasignar={handleAutoasignar} puedeAutoasignar={puedeAutoasignar} />
                <Paginador pagina={paginaActual} totalPaginas={totalPaginas} total={filteredTickets.length} desde={rangoPagina.desde} hasta={rangoPagina.hasta} onChange={setPagina} />
              </>
            )
          )}
        </div>

        {/* Columna Derecha: Panel de Acciones Pendientes y Próximos Eventos (4/12 = 33% o 3/12 = 25%) */}
        <div className="lg:col-span-4 xl:col-span-3">
          <InvGateSidebarActions
            pendingApprovals={derivaciones}
            pendingTasks={tickets.filter(t => t.estado === 'En Proceso').slice(0, 2)}
            dueSoonEvents={dueSoonEvents}
            onResponderDerivacion={handleResponderDerivacion}
            onCompleteTask={handleCompleteTask}
          />
        </div>
      </div>

      {/* Modal para Crear Tickets */}
      <CreateTicketModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onTicketCreated={cargarTickets}
        mode={modalMode}
      />
    </div>
  );
}
